import { FileUpload } from "../../UI/FormFields";

interface Step3Data {
  documentoRGCPF: File | null;
  comprovanteFormacao: File | null;
  comprovanteResidencia: File | null;
}

interface Step3Props {
  errors: Partial<Record<keyof Step3Data, string>>;
  onChange: (name: keyof Step3Data, file: File | null) => void;
  fileNames: {
    documentoRGCPF: string;
    comprovanteFormacao: string;
    comprovanteResidencia: string;
  };
}

export default function Step3DocumentacaoDigital({
  errors,
  onChange,
  fileNames,
}: Step3Props) {
  return (
    <div className="space-y-8 animate-fadeIn">
      <div className="bg-gray-50 p-6 mb-8 rounded-lg shadow-sm">
        <h3 className="text-xl font-bold text-gray-900 mb-2 uppercase tracking-wide">
          Documentação Digital
        </h3>
        <p className="text-sm text-gray-600 mb-3">
          Faça o upload dos documentos solicitados em formato PDF. Tamanho
          máximo: <span className="font-semibold">5MB</span> por arquivo.
        </p>
        <div className="bg-yellow-50 border-l-4 border-yellow-600 p-3 rounded">
          <p className="text-xs text-gray-800 font-medium">
            <strong>Atenção:</strong> Certifique-se de que os documentos estejam
            legíveis e completos antes de enviar.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Documento RG e CPF */}
        <div className="bg-white border border-gray-300 hover:border-blue-900 transition-all duration-200 rounded-lg p-6 shadow-sm hover:shadow-md">
          <FileUpload
            label="Cópias do RG e CPF"
            name="documentoRGCPF"
            onChange={(file) => onChange("documentoRGCPF", file)}
            required
            error={errors.documentoRGCPF}
            accept=".pdf"
            maxSizeMB={5}
            currentFileName={fileNames.documentoRGCPF}
          />
          <div className="mt-3 p-3 bg-gray-50 border-l-4 border-gray-400 rounded">
            <p className="text-xs text-gray-700">
              <span className="font-semibold">Observação:</span> Envie um único
              arquivo PDF contendo cópias legíveis do RG e CPF
            </p>
          </div>
        </div>

        {/* Comprovante de Formação */}
        <div className="bg-white border border-gray-300 hover:border-blue-900 transition-all duration-200 rounded-lg p-6 shadow-sm hover:shadow-md">
          <FileUpload
            label="Comprovante de Conclusão do Curso"
            name="comprovanteFormacao"
            onChange={(file) => onChange("comprovanteFormacao", file)}
            required
            error={errors.comprovanteFormacao}
            accept=".pdf"
            maxSizeMB={5}
            currentFileName={fileNames.comprovanteFormacao}
          />
          <div className="mt-3 p-3 bg-gray-50 border-l-4 border-gray-400 rounded">
            <p className="text-xs text-gray-700">
              <span className="font-semibold">Observação:</span> Diploma,
              certificado ou declaração de conclusão do curso de maior nível
            </p>
          </div>
        </div>

        {/* Comprovante de Residência */}
        <div className="bg-white border border-gray-300 hover:border-blue-900 transition-all duration-200 rounded-lg p-6 shadow-sm hover:shadow-md lg:col-span-2">
          <FileUpload
            label="Comprovante de Residência"
            name="comprovanteResidencia"
            onChange={(file) => onChange("comprovanteResidencia", file)}
            required
            error={errors.comprovanteResidencia}
            accept=".pdf"
            maxSizeMB={5}
            currentFileName={fileNames.comprovanteResidencia}
          />
          <div className="mt-3 p-3 bg-gray-50 border-l-4 border-gray-400 rounded">
            <p className="text-xs text-gray-700">
              <span className="font-semibold">Observação:</span> Conta de luz,
              água, telefone ou outro documento oficial dos últimos 3 meses
            </p>
          </div>
        </div>
      </div>

      {/* Informações Importantes */}
      <div className="bg-gray-50 border-l-4 border-blue-900 rounded p-6 mt-8 shadow-sm">
        <h4 className="text-base font-bold text-gray-900 mb-3 uppercase tracking-wide">
          Informações Importantes
        </h4>
        <ul className="text-sm text-gray-700 space-y-2">
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
              Todos os documentos devem estar em <strong>formato PDF</strong>
            </span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
              Tamanho máximo por arquivo: <strong>5MB</strong>
            </span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
              Certifique-se de que os documentos estão{" "}
              <strong>legíveis e completos</strong>
            </span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>Documentos com rasuras podem ser recusados</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>
              Após finalizar, você poderá visualizar os editais disponíveis
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
}
